import { ApiError } from './helpers'
import firebase from './firebase';
import store from './store';
import { updateToken } from '../redux/auth/actions';

// Treat a cached token as expired slightly early so a request never leaves
// with a token that dies in flight.
const TOKEN_EXPIRY_SKEW_MS = 30 * 1000;

const isTokenExpired = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const { exp } = JSON.parse(atob(padded));
    return !exp || Date.now() >= exp * 1000 - TOKEN_EXPIRY_SKEW_MS;
  } catch (error) {
    return true;
  }
};

// Resolves once the Firebase SDK has restored (or ruled out) a persisted
// session. Right after a page load `firebase.auth().currentUser` is null even
// for a signed-in user until that restore completes — deciding "no user, no
// token" before this point sends requests with no Authorization header and
// produces guaranteed 401s the retry path can't recover from either (it hits
// the same null currentUser). Lazily initialized at first call so importing
// this module stays side-effect free (jest mocks a minimal firebase).
let authInitialized = null;
const waitForAuthInit = () => {
  if (!authInitialized) {
    authInitialized = new Promise((resolve) => {
      const unsubscribe = firebase.auth().onAuthStateChanged(() => {
        unsubscribe();
        resolve();
      });
    });
  }
  return authInitialized;
};

const getToken = async (forceRefresh = false) => {
  try {
    const cachedToken = JSON.parse(localStorage.getItem('state'))?.authReducer?.idToken;

    // Never hand out a token that is already expired — a reopened/idle tab
    // restores a stale token from localStorage, and firing requests with it
    // just produces guaranteed 401s before the retry path kicks in.
    if (!forceRefresh && cachedToken && !isTokenExpired(cachedToken)) {
      return cachedToken;
    }

    // No usable cached token: wait until the SDK has settled the session
    // before reading currentUser (see waitForAuthInit above).
    await waitForAuthInit();
    const user = firebase.auth().currentUser;
    if (!user) {
      return false;
    }

    const refreshedToken = await user.getIdToken(true);
    store.dispatch(updateToken(refreshedToken));
    return refreshedToken;
  } catch (error) {
    return false;
  }
};


// All portal traffic uses the clean /v1 surface on one base URL.
const customFetch = async (url, options) => {
  if (process.env.NODE_ENV !== 'production' && !`${url}`.startsWith('/v1/')) {
    throw new ApiError(`Non-/v1 API path: ${url}`, 0, url, null);
  }

  let token = await getToken();

  const baseUrl = process.env.REACT_APP_API_URL;

  // `retried`: the refresh-and-replay below runs AT MOST ONCE per call. A 401
  // that is not an expired token (reCAPTCHA rejected the login, a revoked
  // session, a route that 401s by design) used to recurse unboundedly here
  // whenever a Firebase session existed to refresh from — 2026-09-18 prod:
  // one browser replayed POST /v1/auth/login 777 times in 8 minutes with the
  // same spent reCAPTCHA token until the tab was closed.
  const fetchWithToken = async (overrideToken, retried = false) => {
    let authorization = {};
    const sentToken = overrideToken || token;
    if (sentToken) {
      authorization = { Authorization: `Bearer ${sentToken}` };
    }

    try {
      const response = await fetch(`${baseUrl}${url}`, {
        ...options,
        headers: { ...(options?.headers || {}), ...authorization },
      });

      // A non-JSON body (an LB/nginx error page, a host that is not
      // api-internal) must not turn a 404/502 into a status-less SyntaxError:
      // the login screen classifies failures by `status`. A 2xx that is not
      // JSON still rejects exactly as before.
      let body = null;
      try {
        body = await response.json();
      } catch (parseError) {
        if (response.ok) {
          throw parseError;
        }
      }
      if (response.ok) {
        return body;
      } else if (response.status === 401 && !retried) {
        // getToken(true) already dispatches updateToken on success. Do NOT
        // dispatch its return value here: on failure it is `false`, and
        // writing that into authReducer.idToken silently logged the user out
        // of the routing layer as a side effect of a failed refresh.
        // Replay once, with a token that differs from the one just rejected;
        // a second 401 is the server's answer and is thrown below.
        const refreshedToken = await getToken(true);
        if (refreshedToken && refreshedToken !== sentToken) {
          return fetchWithToken(refreshedToken, true);
        }
      }

      // Non-JSON body: there is no message to carry, and an empty one makes
      // the error log useless — fall back to the HTTP reason.
      const message = body == null
        ? (response.statusText || `HTTP ${response.status}`)
        : body.message;
      throw new ApiError(message, response.status, response.url, body);
    } catch (e) {
      // AbortError must reject as-is: resolving undefined here would make
      // aborted calls dispatch success actions with empty payloads.
      if (e?.name === 'AbortError') {
        throw e;
      }
      throw new ApiError(e.message, e.status, url, e.body || null);
    }
  };

  return fetchWithToken();
};

export const getNewUsersRegistered = (url, options) => customFetch(url, options);

export const getUnEncryptedUserById = (userId, token) => {
  return customFetch(`/v1/donors/${userId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export const signIn = (email, password, captchaResponse) => {
  return customFetch('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      password,
      ...(captchaResponse && { captchaResponse })
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export const getPasswordPolicy = () => {
  return customFetch('/v1/auth/password-policy', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export const apiForgotPassword = (email, originUrl) => {
  return customFetch('/v1/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({
      email,
      originUrl
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export const getCustomToken = (token) => {
  return customFetch('/v1/auth/token', {
    method: 'POST',
    body: JSON.stringify({ token }),
    headers: {
      'Content-Type': 'application/json'
    }
  });
}

export const getUnEncryptedUserByPnr = (userPnr, token) => {
  // PNR never rides in a URL on the v1 surface.
  return customFetch('/v1/donors/lookup', {
    method: 'POST',
    body: JSON.stringify({ pnr: userPnr }),
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
}

export const editEncryptedUser = (userId, data, token) => {
  return customFetch(`/v1/donors/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
}

// CMT-57: the public donation-site directory — every bank with `id`, `name`,
// `active`, `selectable` (= active), `reference` (the department path
// rhf/{rhf}/hf/{hf}/departments/{dep} as a string) and, since CMT-87,
// `features` ({donationSiteSwitch: bool}, derived from the bank's HF — the
// per-HF switch sysadmin sets; a missing object or key means off). Backs the
// «Valgt sted» dropdown in «Giverdetaljer»: the same-HF siblings of the
// donor's site, including banks the staff member has no grant for.
export const getDonationSites = (options) => customFetch('/v1/bloodbanks', {
  method: 'GET',
  ...options,
});

// CMT-57: move a donor to another ACTIVE donation site in the SAME hospital
// trust (HF) — the server validates (feature on for the HF, same HF, active
// site, no active booking) and bumps `updated`. 200 {message:
// success|unchanged, donationSiteIds, previousBloodbankId?}; the two
// rejections the UI explains are 409 {error: active_booking_exists} and
// (CMT-87, checked first — before the `unchanged` case) 409 {error:
// feature_disabled}. The site change NEVER rides in PUT /v1/donors/{uid}.
export const moveDonorDonationSite = (donorId, bloodbankId) => customFetch(`/v1/donors/${donorId}/donation-site`, {
  method: 'POST',
  body: JSON.stringify({ bloodbankId }),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getReservationsForDay = (url, options) => customFetch(url, options);

export const editReservation = (url, data, userId) => customFetch(url, {
  method: 'PUT',
  body: JSON.stringify({
    donorType: data.donorType,
    donorStatus: data.donorStatus,
    appointmentType: data.appointmentType,
    quarantine: data.quarantine,
    quarantineDesc: data.quarantineDesc,
    lastDonation: data.lastDonation,
    userId
  }),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createReservation = (url, startDate, timeslotId, userId, override = false) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify({startDate, timeslotId, userId, override}),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const preCheckBooking = ({ donorUid, appointmentTypeId, appointmentStartDate, donorType }) => customFetch('/v1/bookings/pre-check', {
  method: 'POST',
  body: JSON.stringify({ donorUid, appointmentTypeId, appointmentStartDate, donorType }),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const overwriteTimeslotAndBook = (url, startDate, timeslotId, userId) => customFetch(url, {
  method: 'PUT',
  body: JSON.stringify({startDate, timeslotId, userId}),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const cancelReservation = (url, reservationId, userId, reservationTimestamp, bloodbankName) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify({reservationId, userId, reservationTimestamp, bloodbankName, pushMsg: true}),
  headers: {
    'Content-Type': 'application/json',
  },
});

// v1: status transition is an action — POST …/reservations/{id}/status
export const updateStatus = (url, status, userStatus) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify({status, userStatus}),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createTimeslot = (data, bloodbankId) => customFetch(`/v1/bloodbanks/${bloodbankId}/timeslots`, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const editTimeslot = (data, bloodbankId) => customFetch(`/v1/bloodbanks/${bloodbankId}/timeslots/${data.timeslotId}`, {
  method: 'PUT',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

// v1: the deleted thing IS the recurrence rule — id in the path, no body.
export const deleteTimeslot = (data, bloodbankId) => customFetch(`/v1/bloodbanks/${bloodbankId}/timeslot-rules/${data.ruleId}`, {
  method: 'DELETE',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getTimetable = (url, options) => customFetch(url,options);
export const get = (url, options) => customFetch(url, options);
export const getEligibleAppointmentTypesForStaff = (donorUid) => customFetch(`/v1/donors/${donorUid}/eligible-appointment-types`);

export const getCalendar = (url, options) => customFetch(url, options);

export const resourceCreate = (url, data) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const resourceEdit = (url, data) => customFetch(url, {
  method: 'PUT',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const resourceDelete = (url) => customFetch(url, {
  method: 'DELETE',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const checkReservationsExists = (timeslotId, bloodbankId) =>
  customFetch(`/v1/bloodbanks/${bloodbankId}/timeslots/${timeslotId}/booking-status`);

export const calcAppointmentsInTimeslot = (timeslotId, bloodbankId) =>
  customFetch(`/v1/bloodbanks/${bloodbankId}/timeslots/${timeslotId}/appointment-counts`);

// v1: staff profile update is PATCH /v1/staff/{id}
export const addPhoneRequest = (url, data) => customFetch(url, {
  method: 'PATCH',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
})

export const getFromUsers = (url, data, options) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
  ...options,
});

export const getUser = (url, data) => customFetch(url, {
  method: 'GET',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getService = (url, data) => customFetch(url, {
  method: 'GET',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createPushMessage = (url, data) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createEmail = (url, data) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createErrorLog = (url, data) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

export const createLoginAttemptLog = (url, data) => customFetch(url, {
  method: 'POST',
  body: JSON.stringify(data),
  headers: {
    'Content-Type': 'application/json',
  },
});

// CMT-17: monthly booking / donor-base statistics for one bank. `from`/`to`
// are YYYY-MM (inclusive, max 24 months). 404 = the caller's `reports` feature
// flag is off, 403 = no access to the bank, 400 = bad period (see the
// Reports page for the mapping).
export const getBloodbankStatistics = (bloodbankId, from, to, options) => {
  const query = new URLSearchParams({ from, to }).toString();
  return customFetch(`/v1/bloodbanks/${bloodbankId}/statistics?${query}`, {
    method: 'GET',
    ...options,
  });
};

export const fetchHolidays = (year) => customFetch(`/v1/holidays?from=${year}`);

export const fetchHolidaysRange = (startYear, endYear) => customFetch(`/v1/holidays?from=${startYear}&to=${endYear}`);



// https://us-central1-labcraft-playground.cloudfunctions.net
// http://localhost:5000/labcraft-playground/us-central1
// http://localhost:5000/labcraft-ekm/us-central1/api
// https://us-central1-labcraft-ekm.cloudfunctions.net
